<section class="migrate-page">

  <!-- HERO -->
  <header class="migrate-hero">
    <div class="migrate-hero__content">
      <div class="migrate-hero__kicker">Migrating from Render</div>
      <h1 class="migrate-hero__title">Migrate from Render to EU infrastructure</h1>
      <p class="migrate-hero__subtitle">
        Partiri supports the same Git-based deployment model as Render. Your build command, start
        command, and environment variables transfer as-is, and if you're already using a
        Dockerfile, it works unchanged — now running entirely on EU infrastructure, with no
        sleep-on-idle behavior.
      </p>
      <div class="migrate-hero__cta">
        <a mat-flat-button routerLink="/register" class="btn-primary">
          <mat-icon>rocket_launch</mat-icon>
          Deploy your first app
        </a>
        <a mat-outlined-button routerLink="/documentation/start" class="btn-ghost">
          <mat-icon>description</mat-icon>
          Read the deploy guide
        </a>
      </div>
    </div>
  </header>

  <!-- WHAT MAPS TO WHAT -->
  <section class="migrate-section" id="mapping">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">What maps to what</div>
      <h2 class="migrate-section__title">Render concepts, translated</h2>
      <p class="migrate-section__subtitle">
        Your build command, start command, and environment variables transfer as-is.
      </p>
    </div>

    <table class="migrate-table">
      <thead>
        <tr>
          <th>On Render</th>
          <th>On Partiri</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Build Command / Start Command</td>
          <td>Build command and run command in the service settings, transferred as-is</td>
        </tr>
        <tr>
          <td>Dockerfile</td>
          <td>Works unchanged — or use a native runtime, detected automatically from files like package.json or requirements.txt</td>
        </tr>
        <tr>
          <td>Environment variables / Environment Groups</td>
          <td>Environment variables, injected at runtime and editable from the service settings page</td>
        </tr>
        <tr>
          <td>Disks</td>
          <td>Persistent Storage — a 1–10 GB disk attached to a service, for file uploads, SQLite databases, or caches</td>
        </tr>
        <tr>
          <td>Web Service / Background Worker / Cron Job</td>
          <td>Web Service, Worker, and Cron Job service types</td>
        </tr>
        <tr>
          <td>Custom Domains &amp; TLS</td>
          <td>Custom domains with TLS certificates provisioned automatically</td>
        </tr>
        <tr>
          <td>Auto-Deploy on push</td>
          <td>A Git push webhook to your configured branch, or a deploy hook for CI pipelines</td>
        </tr>
        <tr>
          <td>Free-tier sleep on idle</td>
          <td>No sleep-on-idle behavior — services run continuously</td>
        </tr>
        <tr>
          <td>Preview Environments</td>
          <td>Not supported — see limitations below</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- LIMITATIONS -->
  <section class="migrate-section" id="limitations">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Honest limitations</div>
      <h2 class="migrate-section__title">What doesn't carry over</h2>
      <p class="migrate-section__subtitle">
        We'd rather tell you upfront than have you find out mid-migration.
      </p>
    </div>

    <ul class="migrate-limitations">
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>No automatic build detection beyond the supported runtimes.</strong> We detect
        Node.js, Python, Go, Rust, Ruby, Elixir, PHP, JVM, .NET, C++ and static sites from your
        project files. Outside those, bring a Dockerfile — it runs unchanged.</p>
      </li>
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>No Preview Environments.</strong> There is no automatic preview environment
        feature for pull requests. Deployments are triggered manually or via Git push webhooks to
        a configured branch.</p>
      </li>
      <li>
        <mat-icon>info</mat-icon>
        <p><strong>Managed PostgreSQL, no Redis.</strong> Partiri offers Managed PostgreSQL
        databases (single-instance, EU-hosted, private in-cluster networking) — there's no
        backups, point-in-time recovery, high availability, or connection pooling in v1, and no
        managed Redis service. Persistent Storage attaches a volume to a service for file-based
        data; for anything needing those guarantees, keep it external and connect via an
        environment variable.</p>
      </li>
    </ul>
  </section>

  <!-- FIRST DEPLOY -->
  <section class="migrate-section" id="first-deploy">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Getting started</div>
      <h2 class="migrate-section__title">Your first deploy, step by step</h2>
      <p class="migrate-section__subtitle">
        Bring the same build and start commands you already use on Render.
      </p>
    </div>

    <ol class="migrate-steps">
      <li>
        <span class="migrate-steps__num">1</span>
        <div>
          <h3 class="migrate-steps__title">Connect your source</h3>
          <p class="migrate-steps__desc">
            Point Partiri at the same Git repository (GitHub, GitLab, Bitbucket, or Codeberg) or
            container image you already deploy from Render.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">2</span>
        <div>
          <h3 class="migrate-steps__title">Reuse your build and start commands</h3>
          <p class="migrate-steps__desc">
            Set the same build command and start command in the service settings, or keep your
            Dockerfile as-is.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">3</span>
        <div>
          <h3 class="migrate-steps__title">Copy your environment variables</h3>
          <p class="migrate-steps__desc">
            Move your Render environment variables into Partiri environment variables — they
            transfer directly, no renaming required.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">4</span>
        <div>
          <h3 class="migrate-steps__title">Pick a region and pod size</h3>
          <p class="migrate-steps__desc">
            Choose a European region and a pod size — all available regions are based in Europe.
          </p>
        </div>
      </li>
      <li>
        <span class="migrate-steps__num">5</span>
        <div>
          <h3 class="migrate-steps__title">Deploy</h3>
          <p class="migrate-steps__desc">
            We build your app, assign it a subdomain, and it's live — running continuously, with
            no sleep-on-idle behavior. Attach your custom domain whenever you're ready.
          </p>
        </div>
      </li>
    </ol>

    <a routerLink="/documentation/start" class="link-btn">
      Read the full deployment guide
      <mat-icon>arrow_forward</mat-icon>
    </a>
  </section>

  <!-- EU RESIDENCY -->
  <section class="migrate-section" id="eu-residency">
    <div class="migrate-section__head">
      <div class="migrate-section__kicker">Data residency</div>
      <h2 class="migrate-section__title">Infrastructure that stays in the EU</h2>
    </div>

    <div class="migrate-residency">
      <p>
        Partiri is a European company, and our infrastructure runs entirely out of European
        regions. Every deployment — build, storage, and traffic — stays within the EU, and we're
        GDPR-compliant by default with no extra configuration needed.
      </p>
      <p>
        Regions are live today in Germany (Nuremberg) and Finland (Helsinki), with more European
        countries on the roadmap.
      </p>
      <a routerLink="/roadmap" class="link-btn">
        See where we're live
        <mat-icon>arrow_forward</mat-icon>
      </a>
    </div>
  </section>

  <!-- CTA -->
  <section class="migrate-section">
    <div class="migrate-cta-banner">
      <h2 class="migrate-cta-banner__title">Ready to move off Render?</h2>
      <p class="migrate-cta-banner__subtitle">
        Bring your build command, start command, and environment variables, and deploy on EU-only
        infrastructure.
      </p>
      <a mat-flat-button routerLink="/register" class="btn-primary btn-primary--lg">
        Create your project
      </a>
    </div>
  </section>

</section>
